import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchGradingTree } from '../../../../services/gradingService';
import type {
  GradingAnswer,
  GradingCalibrationAnswer,
  GradingCalibrationQuestion,
  GradingDiffFactor,
  GradingDrawerState,
  GradingFamily,
  GradingQuestion,
  GradingRole,
  GradingRoleCluster,
  GradingSpecialization,
  GradingTab,
  GradingTreePayload,
  TreeSelection,
} from './types';
import { normalizeGradingTreePayload } from './utils';

type GradingContextValue = {
  tab: GradingTab;
  setTab: (tab: GradingTab) => void;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  families: GradingFamily[];
  roleClusters: GradingRoleCluster[];
  specializations: GradingSpecialization[];
  roles: GradingRole[];
  factors: GradingDiffFactor[];
  questions: GradingQuestion[];
  answers: GradingAnswer[];
  calibrationQuestions: GradingCalibrationQuestion[];
  calibrationAnswers: GradingCalibrationAnswer[];
  selectedFamilyId: number | null;
  setSelectedFamilyId: (id: number | null) => void;
  treeSelection: TreeSelection | null;
  setTreeSelection: (sel: TreeSelection | null) => void;
  drawer: GradingDrawerState;
  openDrawer: (state: GradingDrawerState) => void;
  closeDrawer: () => void;
  search: string;
  setSearch: (v: string) => void;
  expandedNodes: Set<string>;
  toggleNode: (key: string) => void;
};

const GradingContext = createContext<GradingContextValue | null>(null);

function applyPayload(data: GradingTreePayload) {
  return normalizeGradingTreePayload({
    families: data.families || [],
    roleClusters: data.roleClusters || [],
    specializations: data.specializations || [],
    roles: data.roles || [],
    factors: data.factors || [],
    questions: data.questions || [],
    answers: data.answers || [],
    calibrationQuestions: data.calibrationQuestions || [],
    calibrationAnswers: data.calibrationAnswers || [],
  });
}

export function GradingProvider({ children }: { children: React.ReactNode }) {
  const [tab, setTab] = useState<GradingTab>('tree');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [families, setFamilies] = useState<GradingFamily[]>([]);
  const [roleClusters, setRoleClusters] = useState<GradingRoleCluster[]>([]);
  const [specializations, setSpecializations] = useState<GradingSpecialization[]>([]);
  const [roles, setRoles] = useState<GradingRole[]>([]);
  const [factors, setFactors] = useState<GradingDiffFactor[]>([]);
  const [questions, setQuestions] = useState<GradingQuestion[]>([]);
  const [answers, setAnswers] = useState<GradingAnswer[]>([]);
  const [calibrationQuestions, setCalibrationQuestions] = useState<GradingCalibrationQuestion[]>([]);
  const [calibrationAnswers, setCalibrationAnswers] = useState<GradingCalibrationAnswer[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);
  const [treeSelection, setTreeSelection] = useState<TreeSelection | null>(null);
  const [drawer, setDrawer] = useState<GradingDrawerState>(null);
  const [search, setSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchGradingTree();
      const p = applyPayload(data);
      setFamilies(p.families);
      setRoleClusters(p.roleClusters);
      setSpecializations(p.specializations);
      setRoles(p.roles);
      setFactors(p.factors);
      setQuestions(p.questions);
      setAnswers(p.answers);
      setCalibrationQuestions(p.calibrationQuestions);
      setCalibrationAnswers(p.calibrationAnswers);
      setSelectedFamilyId((prev) => (prev && p.families.some((f) => f.id === prev) ? prev : p.families[0]?.id ?? null));
      setTreeSelection((prev) => {
        if (prev?.kind === 'family' && p.families.some((f) => f.id === prev.id)) return prev;
        if (prev?.kind === 'role_cluster' && p.roleClusters.some((s) => s.id === prev.id)) return prev;
        if (prev?.kind === 'role' && p.roles.some((r) => r.id === prev.id)) return prev;
        const first = p.families[0];
        return first ? { kind: 'family', id: first.id } : null;
      });
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        p.roleClusters.forEach((rc) => next.add(`rc-${rc.id}`));
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const toggleNode = useCallback((key: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      tab, setTab, loading, error, reload,
      families, roleClusters, specializations, roles, factors, questions, answers,
      calibrationQuestions, calibrationAnswers,
      selectedFamilyId, setSelectedFamilyId, treeSelection, setTreeSelection,
      drawer, openDrawer: setDrawer, closeDrawer: () => setDrawer(null),
      search, setSearch, expandedNodes, toggleNode,
    }),
    [tab, loading, error, reload, families, roleClusters, specializations, roles, factors, questions, answers, calibrationQuestions, calibrationAnswers, selectedFamilyId, treeSelection, drawer, search, expandedNodes, toggleNode],
  );

  return <GradingContext.Provider value={value}>{children}</GradingContext.Provider>;
}

export function useGrading() {
  const ctx = useContext(GradingContext);
  if (!ctx) throw new Error('useGrading outside provider');
  return ctx;
}
